import { TAB_BOTTOM_SPACING, TabScreenWrapper } from '@/components/layout/tab-screen-wrapper';
import { Text } from '@/components/ui/text';
import { Notification } from '@/types';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { SectionList, View } from 'react-native';
import { NotificationItem } from './components/notification-item';
import { NotificationsHeader } from './components/notifications-header';
import { groupNotificationsByTime } from './utils/group-notifications-by-time';

interface NotificationsScreenProps {
  notifications: Notification[];
  actionableFriendRequestNotificationIds?: Set<string>;
  isLoading?: boolean;
  error?: Error | string | null;
  onMarkAsRead?: (notificationId: string) => void;
  onMarkAllAsRead?: () => void;
  onNotificationPress?: (notification: Notification) => void;
  onAcceptFriendRequest?: (notificationId: string, requesterId: string) => void;
  onRejectFriendRequest?: (notificationId: string, requesterId: string) => void;
  processingFriendRequestIds?: Set<string>;
}

export function NotificationsScreen({
  notifications,
  actionableFriendRequestNotificationIds = new Set(),
  isLoading = false,
  error = null,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationPress,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  processingFriendRequestIds = new Set(),
}: NotificationsScreenProps) {
  const unreadCount = notifications.filter((n) => !n.read).length;
  const sections = groupNotificationsByTime(notifications);

  return (
    <TabScreenWrapper>
      <NotificationsHeader
        unreadCount={unreadCount}
        onMarkAllAsRead={onMarkAllAsRead}
      />

      {notifications.length > 0 ? (
        <SectionList
          sections={sections}
          keyExtractor={(item) => item.id}
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: TAB_BOTTOM_SPACING }}
          stickySectionHeadersEnabled={false}
          renderSectionHeader={({ section }) => (
            <Text className="text-xs font-semibold tracking-wider text-muted-foreground mt-6 mb-1 px-4">
              {section.title}
            </Text>
          )}
          renderItem={({ item: notification }) => (
            <NotificationItem
              notification={notification}
              canRespondToFriendRequest={
                notification.type === 'friend_request' &&
                actionableFriendRequestNotificationIds.has(notification.id)
              }
              onPress={onNotificationPress}
              onMarkAsRead={onMarkAsRead}
              onAcceptFriendRequest={onAcceptFriendRequest}
              onRejectFriendRequest={onRejectFriendRequest}
              isProcessingFriendRequest={processingFriendRequestIds.has(notification.id)}
            />
          )}
        />
      ) : (
        <View className="flex-1 items-center justify-center px-8">
          <Ionicons
            name="notifications-off-outline"
            size={64}
            className="text-muted-foreground"
          />
          <Text className="mt-4 text-center text-muted-foreground text-sm">
            No notifications yet.
            {'\n'}
            We&apos;ll notify you when something happens!
          </Text>
        </View>
      )}
    </TabScreenWrapper>
  );
}
